import { EMPTY_MONEY_DRAFT, type MoneyDraft } from '@/components/work-orders/MoneyField'
import { cleanDrafts, type OriginalCodeDraft } from '@/components/work-orders/OriginalCodes'
import { MAX_ORIGINAL_CODE_LENGTH, MAX_ORIGINAL_CODES, type Money, type MoneyInput } from '@/domain/work-order.types'
import { moneyInputText, parseMoney } from '@/lib/money'

/** The form's money fields, which share one parse and one message. */
export const MONEY_FIELDS = ['budget', 'plannedCost', 'finalCost'] as const
export const MONEY_LABELS: Record<(typeof MONEY_FIELDS)[number], string> = {
	budget: 'Budget',
	plannedCost: 'Planned cost',
	finalCost: 'Final cost',
}

/** A money draft as the request carries it; blank is null. Only called once `validateForm` passed. */
export function moneyOf(draft: MoneyDraft): MoneyInput | null {
	const parsed = parseMoney(draft.amount, draft.currency)
	return parsed === 'invalid' ? null : parsed
}

/** The first rule the original codes break, if any: too many, too long, or one listed twice. */
export function originalCodesError(drafts: OriginalCodeDraft[]): string | undefined {
	const codes = cleanDrafts(drafts).map((draft) => draft.code)
	if (codes.length > MAX_ORIGINAL_CODES) return `At most ${MAX_ORIGINAL_CODES} original codes`
	if (codes.some((code) => code.length > MAX_ORIGINAL_CODE_LENGTH))
		return `An original code is at most ${MAX_ORIGINAL_CODE_LENGTH} characters`
	const repeated = codes.find((code, index) => codes.indexOf(code) !== index)
	return repeated ? `${repeated} is listed twice` : undefined
}

/** Stored money as the text and currency its field shows. */
export function moneyDraftOf(money: Money | null): MoneyDraft {
	return money ? { amount: moneyInputText(money), currency: money.currency } : EMPTY_MONEY_DRAFT
}
